import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

const FADE_AFTER_MS = 3000;
const FADE_DURATION_MS = 600;

/**
 * Brutalist "Welcome back, {name}" pill that fades in next to the navbar logo
 * on successful sign-in, then fades out after 3 seconds. Fires on a global
 * `fitsme:signed-in` CustomEvent so AuthContext can trigger it from any
 * auth path (magic-link, Google, future social providers) without prop-drilling.
 *
 * The pill never appears on app-bootstrap rehydration — only on a *fresh*
 * sign-in click. This keeps it a moment of confirmation, not chrome.
 */
export const WelcomeBackPill = () => {
    const [user, setUser] = useState(null);
    const [fadingOut, setFadingOut] = useState(false);

    useEffect(() => {
        const onSignedIn = (e) => {
            setUser(e.detail || null);
            setFadingOut(false);
            const fadeTimer = setTimeout(() => setFadingOut(true), FADE_AFTER_MS);
            const hideTimer = setTimeout(() => setUser(null), FADE_AFTER_MS + FADE_DURATION_MS);
            return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
        };
        window.addEventListener("fitsme:signed-in", onSignedIn);
        return () => window.removeEventListener("fitsme:signed-in", onSignedIn);
    }, []);

    if (!user) return null;

    const displayName = user.first_name
        || (user.email ? user.email.split("@")[0] : "stranger");

    return (
        <div
            data-testid="welcome-back-pill"
            aria-live="polite"
            aria-atomic="true"
            className={`hidden md:inline-flex items-center gap-1.5 border-2 border-black bg-[#FF00FF] text-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest shadow-brutal-sm transition-opacity duration-[${FADE_DURATION_MS}ms] ease-out ${fadingOut ? "opacity-0" : "opacity-100"}`}
            style={{ transitionDuration: `${FADE_DURATION_MS}ms` }}
        >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="font-bold">Welcome back, {displayName}</span>
        </div>
    );
};
